<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <AdminSidebar :sidebarOpen="sidebarOpen" currentPage="payroll" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Top Navbar -->
      <AdminNavbar 
        :title="$t('payroll.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('payroll.pendingPayment') }}</p>
                    <h3 class="mb-0 fw-bold">{{ pendingPaymentCount }}</h3>
                    <small class="text-muted"
                      >{{ pendingPaymentHours }}h {{ $t('payroll.total') }}</small
                    >
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-clock-history"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022l-.074.997zm2.004.45a7.003 7.003 0 0 0-.985-.299l.219-.976c.383.086.76.2 1.126.342l-.36.933zm1.37.71a7.01 7.01 0 0 0-.439-.27l.493-.87a8.025 8.025 0 0 1 .979.654l-.615.789a6.996 6.996 0 0 0-.418-.302zm1.834 1.79a6.99 6.99 0 0 0-.653-.796l.724-.69c.27.285.52.59.747.91l-.818.576zm.744 1.352a7.08 7.08 0 0 0-.214-.468l.893-.45a7.976 7.976 0 0 1 .45 1.088l-.95.313a7.023 7.023 0 0 0-.179-.483zm.53 2.507a6.991 6.991 0 0 0-.1-1.025l.985-.17c.067.386.106.778.116 1.17l-1 .025zm-.131 1.538c.033-.17.06-.339.081-.51l.993.123a7.957 7.957 0 0 1-.23 1.155l-.964-.267c.046-.165.086-.332.12-.501zm-.952 2.379c.184-.29.346-.594.486-.908l.914.405c-.16.36-.345.706-.555 1.038l-.845-.535zm-.964 1.205c.122-.122.239-.248.35-.378l.758.653a8.073 8.073 0 0 1-.401.432l-.707-.707z"
                      />
                      <path
                        d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0v1z"
                      />
                      <path
                        d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('payroll.totalAmountDue') }}</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(totalAmountDue) }}
                    </h3>
                    <small class="text-muted"
                      >{{ pendingPaymentCount }} {{ $t('payroll.timesheets') }}</small
                    >
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-cash-stack"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M1 3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1H1zm7 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
                      />
                      <path
                        d="M0 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V5zm3 0a2 2 0 0 1-2 2v4a2 2 0 0 1 2 2h10a2 2 0 0 1 2-2V7a2 2 0 0 1-2-2H3z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-info">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('payroll.paidThisMonth') }}</p>
                    <h3 class="mb-0 fw-bold">
                      {{ formatCurrency(paidThisMonth) }}
                    </h3>
                    <small class="text-muted"
                      >{{ paidThisMonthCount }} {{ $t('payroll.payments') }}</small
                    >
                  </div>
                  <div class="stat-icon bg-info">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-check-circle-fill"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                  />
                </svg>
              </span>
              <input
                type="text"
                class="form-control"
                :placeholder="$t('payroll.searchPlaceholder')"
                v-model="searchQuery"
              />
            </div>
          </div>
          <div class="col-md-3 mb-3 mb-md-0">
            <select class="form-select" v-model="statusFilter">
              <option value="pending">{{ $t('payroll.pendingPayment') }}</option>
              <option value="paid">{{ $t('payroll.paid') }}</option>
              <option value="all">{{ $t('common.all') }}</option>
            </select>
          </div>
          <div class="col-md-5 text-end">
            <button
              class="btn btn-primary"
              @click="processPayment"
              :disabled="selectedPayments.length === 0 || loading"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-credit-card"
                viewBox="0 0 16 16"
              >
                <path
                  d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v1h14V4a1 1 0 0 0-1-1H2zm13 4H1v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V7z"
                />
                <path
                  d="M2 10a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-1z"
                />
              </svg>
              {{ $t('payroll.processPayment', { count: selectedPayments.length }) }}
            </button>
          </div>
        </div>

        <!-- Payroll Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-0">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
              <p class="mt-3 text-muted">{{ $t('payroll.loadingPayroll') }}</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="alert alert-danger m-3">
              {{ error }}
              <button
                @click="loadPayrollData"
                class="btn btn-sm btn-danger ms-2"
              >
                {{ $t('common.retry') }}
              </button>
            </div>

            <!-- Empty State -->
            <div
              v-else-if="payrollRecords.length === 0"
              class="text-center py-5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                fill="currentColor"
                class="bi bi-inbox text-muted mb-3"
                viewBox="0 0 16 16"
              >
                <path
                  d="M4.98 4a.5.5 0 0 0-.39.188L1.54 8H6a.5.5 0 0 1 .5.5 1.5 1.5 0 1 0 3 0A.5.5 0 0 1 10 8h4.46l-3.05-3.812A.5.5 0 0 0 11.02 4H4.98zm9.954 5H10.45a2.5 2.5 0 0 1-4.9 0H1.066l.32 2.562a.5.5 0 0 0 .497.438h12.234a.5.5 0 0 0 .496-.438L14.933 9zM3.809 3.563A1.5 1.5 0 0 1 4.981 3h6.038a1.5 1.5 0 0 1 1.172.563l3.7 4.625a.5.5 0 0 1 .105.374l-.39 3.124A1.5 1.5 0 0 1 14.117 13H1.883a1.5 1.5 0 0 1-1.489-1.314l-.39-3.124a.5.5 0 0 1 .106-.374l3.7-4.625z"
                />
              </svg>
              <h5 class="text-muted">{{ $t('payroll.noRecordsFound') }}</h5>
              <p class="text-muted">
                {{ $t('payroll.noRecordsMessage', { status: statusFilter }) }}
              </p>
            </div>

            <!-- Data Table -->
            <div v-else class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th width="40">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        @change="toggleSelectAll"
                        :checked="isAllSelected"
                      />
                    </th>
                    <th @click="sortBy('employeeName')" class="sortable">
                      {{ $t('timesheets.employee') }}
                      <svg
                        v-if="sortField === 'employeeName'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th @click="sortBy('weekStart')" class="sortable">
                      {{ $t('timesheets.week') }}
                      <svg
                        v-if="sortField === 'weekStart'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th @click="sortBy('totalHours')" class="sortable">
                      {{ $t('payroll.hours') }}
                      <svg
                        v-if="sortField === 'totalHours'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th>{{ $t('common.payRate') }}</th>
                    <th @click="sortBy('totalAmount')" class="sortable">
                      {{ $t('payroll.grossAmount') }}
                      <svg
                        v-if="sortField === 'totalAmount'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th>{{ $t('payroll.advances') }}</th>
                    <th>{{ $t('payroll.netPayment') }}</th>
                    <th>{{ $t('common.status') }}</th>
                    <th @click="sortBy('approvedDate')" class="sortable">
                      {{ $t('timesheets.approved') }}
                      <svg
                        v-if="sortField === 'approvedDate'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payment in paginatedPayments" :key="payment.id">
                    <td>
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :value="payment.id"
                        v-model="selectedPayments"
                        :disabled="payment.status === 'paid'"
                      />
                    </td>
                    <td class="fw-semibold">{{ payment.employeeName }}</td>
                    <td>
                      {{ formatWeek(payment.weekStart, payment.weekEnd) }}
                      <span
                        v-if="payment.weekInProgress"
                        class="badge bg-info text-dark d-block mt-1"
                      >
                        {{ $t('payroll.weekInProgress') }}
                      </span>
                    </td>
                    <td>
                      {{ payment.totalHours }}h
                      <small class="d-block text-muted">
                        {{ payment.payableDays }}
                        {{ $t('payroll.payableDays').toLowerCase() }}
                      </small>
                      <!-- Days still awaiting approval are not in this payment -->
                      <small v-if="payment.pendingDays > 0" class="d-block text-warning">
                        {{ $t('payroll.daysPending', { count: payment.pendingDays }) }}
                      </small>
                    </td>
                    <td>{{ formatCurrency(payment.payRate) }}/h</td>
                    <td class="fw-bold">
                      {{ formatCurrency(payment.totalAmount) }}
                    </td>
                    <td class="text-danger">
                      <span
                        v-if="
                          getEmployeeAdvances(payment.employeeName).length > 0
                        "
                      >
                        -{{
                          formatCurrency(
                            getEmployeeAdvancesTotal(payment.employeeName)
                          )
                        }}
                        <small class="d-block text-muted"
                          >({{
                            getEmployeeAdvances(payment.employeeName).length
                          }}
                          advance{{
                            getEmployeeAdvances(payment.employeeName).length > 1
                              ? "s"
                              : ""
                          }})</small
                        >
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="fw-bold text-success">
                      {{
                        formatCurrency(
                          payment.totalAmount -
                            getEmployeeAdvancesTotal(payment.employeeName)
                        )
                      }}
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="
                          payment.status === 'paid' ? 'bg-info' : 'bg-warning'
                        "
                      >
                        {{ payment.status === "paid" ? $t('payroll.paid') : $t('payroll.pending') }}
                      </span>
                    </td>
                    <td>{{ formatDate(payment.approvedDate) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="row mt-3" v-if="totalPages > 1">
          <div class="col">
            <nav>
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="currentPage--"
                    >{{ $t('common.previous') }}</a
                  >
                </li>
                <li
                  v-for="page in totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: currentPage === page }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="currentPage = page"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <a class="page-link" href="#" @click.prevent="currentPage++"
                    >{{ $t('common.next') }}</a
                  >
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Confirmation Modal -->
    <div
      class="modal fade"
      :class="{ show: showPaymentModal, 'd-block': showPaymentModal }"
      tabindex="-1"
      v-if="showPaymentModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('payroll.processPaymentConfirmation') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closePaymentModal"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-3" v-if="currentPayment">
              {{ $t('payroll.processPaymentFor') }}
              <strong>{{ currentPayment.employeeName }}</strong>
              <br />
              <small class="text-muted"
                >{{ $t('timesheets.week') }}:
                {{
                  formatWeek(currentPayment.weekStart, currentPayment.weekEnd)
                }}</small
              >
              <br />
              <small class="text-muted">
                {{
                  $t('payroll.payingDays', {
                    count: currentPayment.payableDays,
                    hours: currentPayment.totalHours
                  })
                }}
              </small>
            </p>

            <!-- Days awaiting approval roll over to a later payslip -->
            <div
              v-if="currentPayment && currentPayment.pendingDays > 0"
              class="alert alert-warning py-2"
            >
              {{ $t('payroll.daysPending', { count: currentPayment.pendingDays }) }}
            </div>

            <div
              v-if="currentPayment && currentPayment.paidDays > 0"
              class="alert alert-info py-2"
            >
              {{ $t('payroll.topUpNotice') }}
            </div>

            <div
              v-if="currentPayment && currentPayment.weekInProgress"
              class="alert alert-info py-2"
            >
              {{ $t('payroll.weekInProgressWarning') }}
            </div>

            <div class="payment-breakdown border rounded p-3 bg-light">
              <div class="d-flex justify-content-between mb-2">
                <span class="text-muted">{{ $t('payroll.grossAmount') }}:</span>
                <span class="fw-bold">{{
                  formatCurrency(paymentBreakdown.grossAmount)
                }}</span>
              </div>

              <!-- Show advances being deducted -->
              <div v-if="employeeAdvances.length > 0" class="mb-2">
                <div class="d-flex justify-content-between text-danger">
                  <span>{{ $t('payroll.advancesDeduction') }}:</span>
                  <span class="fw-bold"
                    >-{{
                      formatCurrency(paymentBreakdown.advancesDeduction)
                    }}</span
                  >
                </div>
                <div class="ms-3 mt-1">
                  <small
                    v-for="advance in employeeAdvances"
                    :key="advance.id"
                    class="d-block text-muted"
                  >
                    • {{ formatCurrency(advance.amount) }} -
                    {{ advance.reason }} ({{
                      formatDate(advance.approvedDate)
                    }})
                  </small>
                </div>
              </div>
              <div
                v-else
                class="d-flex justify-content-between mb-2 text-muted"
              >
                <span>{{ $t('payroll.advancesDeduction') }}:</span>
                <span>€0.00</span>
              </div>

              <hr class="my-2" />
              <div class="d-flex justify-content-between">
                <span class="fw-bold text-success">{{ $t('payroll.netPayment') }}:</span>
                <span class="fw-bold fs-5 text-success">{{
                  formatCurrency(paymentBreakdown.netPayment)
                }}</span>
              </div>
            </div>

            <div
              v-if="employeeAdvances.length > 0"
              class="alert alert-info mt-3 mb-0"
            >
              <small>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-info-circle-fill me-1"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
                  />
                </svg>
                {{ $t('payroll.advancesRecoveryInfo', { count: employeeAdvances.length }) }}
              </small>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closePaymentModal"
              :disabled="loading"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="confirmPayment"
              :disabled="loading"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-check-lg me-1"
                viewBox="0 0 16 16"
              >
                <path
                  d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"
                />
              </svg>
              {{ loading ? $t('payroll.processing') : $t('payroll.confirmPayment') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showPaymentModal }"
      v-if="showPaymentModal"
    ></div>
  </div>
</template>

<script>
import axios from "axios";
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminNavbar from "@/components/AdminNavbar.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "Payroll",
  components: {
    AdminSidebar,
    AdminNavbar,
  },
  data() {
    return {
      sidebarOpen: true,
      searchQuery: "",
      statusFilter: "pending",
      sortField: "approvedDate",
      sortDirection: "desc",
      currentPage: 1,
      itemsPerPage: 10,
      selectedPayments: [],
      showPaymentModal: false,
      currentPayment: null,
      employeeAdvances: [],
      paymentBreakdown: {
        grossAmount: 0,
        advancesDeduction: 0,
        netPayment: 0,
      },
      loading: false,
      error: null,
      statistics: {
        pendingPayment: { count: 0, hours: 0, amount: 0 },
        paidThisMonth: { count: 0, amount: 0 },
      },
      payrollRecords: [],
      advances: [],
    };
  },
  mounted() {
    this.loadPayrollData();
    this.loadStatistics();
  },
  computed: {
    pendingPaymentCount() {
      return this.statistics.pendingPayment.count;
    },
    pendingPaymentHours() {
      return this.statistics.pendingPayment.hours;
    },
    totalAmountDue() {
      return this.statistics.pendingPayment.amount;
    },
    paidThisMonth() {
      return this.statistics.paidThisMonth.amount;
    },
    paidThisMonthCount() {
      return this.statistics.paidThisMonth.count;
    },
    filteredPayments() {
      let filtered = this.payrollRecords;

      // Filter by status
      // Note: 'pending' filter shows 'approved' timesheets (approved but not paid)
      if (this.statusFilter === "pending") {
        filtered = filtered.filter((p) => p.status === "approved");
      } else if (this.statusFilter === "paid") {
        filtered = filtered.filter((p) => p.status === "paid");
      }
      // 'all' shows everything

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((p) =>
          p.employeeName.toLowerCase().includes(query)
        );
      }

      // Sorting
      filtered.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];

        if (
          this.sortField === "weekStart" ||
          this.sortField === "approvedDate"
        ) {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (aVal < bVal) return this.sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === "asc" ? 1 : -1;
        return 0;
      });

      return filtered;
    },
    paginatedPayments() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredPayments.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredPayments.length / this.itemsPerPage);
    },
    isAllSelected() {
      const pendingPayments = this.paginatedPayments.filter(
        (p) => p.status === "pending"
      );
      return (
        pendingPayments.length > 0 &&
        pendingPayments.every((p) => this.selectedPayments.includes(p.id))
      );
    },
  },
  methods: {
    async loadPayrollData() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem("token");

        if (!token) {
          this.$router.push("/");
          return;
        }

        const response = await axios.get(
          `${API_URL}/api/admin/payroll?status=${this.statusFilter}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          // Parse and normalize data
          this.payrollRecords = response.data.data.timesheets.map((t) => ({
            ...t,
            // totalHours carries the payable (approved, unpaid) hours
            totalHours: parseFloat(t.totalHours),
            payRate: parseFloat(t.payRate),
            totalAmount: parseFloat(t.totalAmount),
            payableHours: parseFloat(t.payableHours),
            payableDays: parseInt(t.payableDays),
            pendingHours: parseFloat(t.pendingHours),
            pendingDays: parseInt(t.pendingDays),
            paidHours: parseFloat(t.paidHours),
            paidDays: parseInt(t.paidDays),
          }));
          this.advances = response.data.data.advances.map((a) => ({
            ...a,
            amount: parseFloat(a.amount),
          }));
        }
      } catch (error) {
        console.error("Load payroll data error:", error);
        this.error = "Failed to load payroll data";
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("token");
          this.$router.push("/");
        }
      } finally {
        this.loading = false;
      }
    },
    async loadStatistics() {
      try {
        const token = localStorage.getItem("token");

        if (!token) return;

        const response = await axios.get(`${API_URL}/api/admin/payroll/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          this.statistics = response.data.data;
        }
      } catch (error) {
        console.error("Load statistics error:", error);
      }
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    formatWeek(start, end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const options = { month: "short", day: "numeric" };
      return `${startDate.toLocaleDateString(
        "en-US",
        options
      )} - ${endDate.toLocaleDateString("en-US", options)}`;
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString("en-GB");
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(amount);
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "asc";
      }
    },
    toggleSelectAll(event) {
      const pendingPayments = this.paginatedPayments.filter(
        (p) => p.status === "pending"
      );
      if (event.target.checked) {
        this.selectedPayments = [
          ...new Set([
            ...this.selectedPayments,
            ...pendingPayments.map((p) => p.id),
          ]),
        ];
      } else {
        const pendingIds = pendingPayments.map((p) => p.id);
        this.selectedPayments = this.selectedPayments.filter(
          (id) => !pendingIds.includes(id)
        );
      }
    },
    async processPayment() {
      if (this.selectedPayments.length === 0) return;

      if (this.selectedPayments.length > 1) {
        alert("Please select only one payment at a time to process");
        return;
      }

      const timesheetId = this.selectedPayments[0];
      const payment = this.payrollRecords.find((p) => p.id === timesheetId);

      if (!payment) return;

      try {
        this.loading = true;
        this.currentPayment = payment;
        const token = localStorage.getItem("token");

        // Fetch outstanding advances for this employee
        const response = await axios.get(
          `${API_URL}/api/admin/payroll/employee/${payment.employeeId}/advances`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          this.employeeAdvances = response.data.data.advances;
          const totalAdvances = response.data.data.totalAmount;
          const totalNet = payment.totalAmount - totalAdvances;

          // Set payment breakdown and show modal
          this.paymentBreakdown = {
            grossAmount: payment.totalAmount,
            advancesDeduction: totalAdvances,
            netPayment: totalNet,
          };
          this.showPaymentModal = true;
        }
      } catch (error) {
        console.error("Fetch employee advances error:", error);
        alert("Failed to fetch employee advances. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    closePaymentModal() {
      this.showPaymentModal = false;
      this.currentPayment = null;
      this.employeeAdvances = [];
      this.paymentBreakdown = {
        grossAmount: 0,
        advancesDeduction: 0,
        netPayment: 0,
      };
    },
    async confirmPayment() {
      if (this.selectedPayments.length === 0) return;

      try {
        this.loading = true;
        const token = localStorage.getItem("token");
        const timesheetId = this.selectedPayments[0];

        const response = await axios.post(
          `${API_URL}/api/admin/payroll/${timesheetId}/process-payment`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          alert(
            `Payment processed successfully!\n\n` +
              `Employee: ${response.data.data.employeeName}\n` +
              `Gross Amount: ${this.formatCurrency(
                response.data.data.grossAmount
              )}\n` +
              `Advances Deducted: ${this.formatCurrency(
                response.data.data.advancesDeducted
              )}\n` +
              `Net Payment: ${this.formatCurrency(
                response.data.data.netPayment
              )}`
          );

          // Reload data
          await this.loadPayrollData();
          await this.loadStatistics();

          this.selectedPayments = [];
          this.closePaymentModal();
        }
      } catch (error) {
        console.error("Process payment error:", error);
        alert(
          error.response?.data?.message ||
            "Failed to process payment. Please try again."
        );
      } finally {
        this.loading = false;
      }
    },
    getEmployeeAdvances(employeeName) {
      return this.advances.filter(
        (a) =>
          a.employeeName === employeeName &&
          a.status === "approved" &&
          !a.adjustedInPayroll
      );
    },
    getEmployeeAdvancesTotal(employeeName) {
      return this.getEmployeeAdvances(employeeName).reduce(
        (sum, a) => sum + a.amount,
        0
      );
    },
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
  },
  watch: {
    statusFilter() {
      this.currentPage = 1;
      this.loadPayrollData();
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

/* Main Content */
.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Statistics Cards */
.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Table */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef !important;
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
